<template>
  <nav class="nav-bar" v-if="authStore.isAuthenticated">
    <div class="nav-container">
      <!-- Logo/Brand -->
      <div class="nav-brand">
        <h2>📦 Inventory System</h2>
        <span class="user-info">
          {{ authStore.user?.role }} - {{ authStore.user?.email }}
        </span>
      </div>

      <!-- Navigation Menu -->
      <div class="nav-menu">
        <router-link
          v-for="item in navigation.getMenuItems()"
          :key="item.name"
          :to="{ name: item.name }"
          class="nav-item"
          :class="{ active: $route.name === item.name }"
        >
          <span class="nav-icon">{{ item.icon }}</span>
          <span class="nav-label">{{ item.label }}</span>
        </router-link>
      </div>

      <!-- User Actions -->
      <div class="nav-actions">
        <div class="role-badge" :class="roleClass">
          {{ authStore.user?.role }}
        </div>
        <button @click="handleLogout" class="logout-btn">
          🚪 Logout
        </button>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { createNavigation } from '../utils/routeGuards'

const router = useRouter()
const authStore = useAuthStore()
const navigation = createNavigation(router, authStore)

const roleClass = computed(() => {
  return authStore.user?.role?.toLowerCase() || 'unknown'
})

const handleLogout = () => {
  authStore.logout()
  router.push({ name: 'Login' })
}
</script>

<style scoped>
.nav-bar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.75rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}

.nav-brand h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.user-info {
  font-size: 0.75rem;
  opacity: 0.9;
  display: block;
  margin-top: 0.25rem;
}

.nav-menu {
  display: flex;
  gap: 0.5rem;
  flex: 1;
  justify-content: center;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  text-decoration: none;
  color: white;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-weight: 500;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-1px);
}

.nav-item.active {
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.nav-icon {
  font-size: 1.1rem;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.role-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.role-badge.owner {
  background: #ff6b6b;
  color: white;
}

.role-badge.cashier {
  background: #4ecdc4;
  color: white;
}

.role-badge.unknown {
  background: #95a5a6;
  color: white;
}

.logout-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

/* Responsive design */
@media (max-width: 768px) {
  .nav-container {
    flex-direction: column;
    gap: 1rem;
  }
  
  .nav-brand {
    text-align: center;
  }
  
  .nav-menu {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .nav-item {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
  
  .user-info {
    font-size: 0.7rem;
  }
}

@media (max-width: 480px) {
  .nav-menu {
    gap: 0.25rem;
  }
  
  .nav-item {
    flex-direction: column;
    padding: 0.4rem;
    min-width: 60px;
  }
  
  .nav-label {
    font-size: 0.7rem;
  }
  
  .nav-actions {
    gap: 0.5rem;
  }
}
</style> 